/* eslint-disable */

/**
 * Ball set manifest containing all available ball sets and their configurations.
 * This defines what ball sets are available, their styles, and which balls
 * are included in each set.
 *
 * @package Asset/Data
 * @project ChalkySticks Pad
 */

/**
 * Interface for individual ball configuration
 */
export interface IBallConfig {
	/**
	 * The key identifier for this ball (e.g., 'cue', 'one', 'two', etc.)
	 */
	key: string;

	/**
	 * The file path relative to the ball set directory
	 */
	path: string;

	/**
	 * Display name for the ball
	 */
	name: string;

	/**
	 * Optional color hex code for the ball
	 */
	color?: string;
}

/**
 * Interface for ball set configuration
 */
export interface IBallSetConfig {
	/**
	 * Unique identifier for the ball set
	 */
	id: string;

	/**
	 * Display name for the ball set
	 */
	name: string;

	/**
	 * Description of the ball set
	 */
	description: string;

	/**
	 * The directory path where the ball images are stored
	 */
	directory: string;

	/**
	 * The game type this set is primarily used for
	 */
	gameType: 'pool' | 'snooker' | 'billiards' | 'carom';

	/**
	 * Array of balls included in this set
	 */
	balls: IBallConfig[];

	/**
	 * Whether this set is currently available/active
	 */
	active: boolean;

	/**
	 * Optional preview image for the set
	 */
	preview?: string;
}

/**
 * Complete manifest of all available ball sets
 */
export const BALL_SETS: Record<string, IBallSetConfig> = {
	'cyclop-pool': {
		active: true,
		balls: [
			{ key: 'ghost', name: 'Ghost Ball', path: 'ball-ghost.svg' },
			{ key: 'cue', name: 'Cue Ball', path: 'ball-cue.svg' },
			{ key: '1', name: '1 Ball', path: 'ball-1.svg', color: '#FFD700' },
			{ key: '2', name: '2 Ball', path: 'ball-2.svg', color: '#0000FF' },
			{ key: '3', name: '3 Ball', path: 'ball-3.svg', color: '#FF0000' },
			{ key: '4', name: '4 Ball', path: 'ball-4.svg', color: '#800080' },
			{ key: '5', name: '5 Ball', path: 'ball-5.svg', color: '#FF4500' },
			{ key: '6', name: '6 Ball', path: 'ball-6.svg', color: '#228B22' },
			{ key: '7', name: '7 Ball', path: 'ball-7.svg', color: '#8B0000' },
			{ key: '8', name: '8 Ball', path: 'ball-8.svg', color: '#000000' },
			{ key: '9', name: '9 Ball', path: 'ball-9.svg', color: '#FFD700' },
			{ key: '10', name: '10 Ball', path: 'ball-10.svg', color: '#0000FF' },
			{ key: '11', name: '11 Ball', path: 'ball-11.svg', color: '#8B0000' },
			{ key: '12', name: '12 Ball', path: 'ball-12.svg', color: '#800080' },
			{ key: '13', name: '13 Ball', path: 'ball-13.svg', color: '#FF4500' },
			{ key: '14', name: '14 Ball', path: 'ball-14.svg', color: '#228B22' },
			{ key: '15', name: '15 Ball', path: 'ball-15.svg', color: '#8B0000' },
		],
		description: 'Professional Cyclop pool balls with vibrant colors',
		directory: 'cyclop',
		gameType: 'pool',
		id: 'cyclop-pool',
		name: 'Cyclop Pool',
	},

	'chalkysticks-pool': {
		active: true,
		balls: [
			{ key: 'ghost', name: 'Ghost Ball', path: 'ball-ghost.svg' },
			{ key: 'cue', name: 'Cue Ball', path: 'ball-cue.svg' },
			{ key: '1', name: '1 Ball', path: 'ball-1.svg', color: '#FFD700' },
			{ key: '2', name: '2 Ball', path: 'ball-2.svg', color: '#0000FF' },
			{ key: '3', name: '3 Ball', path: 'ball-3.svg', color: '#FF0000' },
			{ key: '4', name: '4 Ball', path: 'ball-4.svg', color: '#800080' },
			{ key: '5', name: '5 Ball', path: 'ball-5.svg', color: '#FF4500' },
			{ key: '6', name: '6 Ball', path: 'ball-6.svg', color: '#228B22' },
			{ key: '7', name: '7 Ball', path: 'ball-7.svg', color: '#8B0000' },
			{ key: '8', name: '8 Ball', path: 'ball-8.svg', color: '#000000' },
			{ key: '9', name: '9 Ball', path: 'ball-9.svg', color: '#FFD700' },
			{ key: '10', name: '10 Ball', path: 'ball-10.svg', color: '#0000FF' },
			{ key: '11', name: '11 Ball', path: 'ball-11.svg', color: '#8B0000' },
			{ key: '12', name: '12 Ball', path: 'ball-12.svg', color: '#800080' },
			{ key: '13', name: '13 Ball', path: 'ball-13.svg', color: '#FF4500' },
			{ key: '14', name: '14 Ball', path: 'ball-14.svg', color: '#228B22' },
			{ key: '15', name: '15 Ball', path: 'ball-15.svg', color: '#8B0000' },
		],
		description: 'ChalkySticks custom designed pool balls',
		directory: 'chalkysticks',
		gameType: 'pool',
		id: 'chalkysticks-pool',
		name: 'ChalkySticks Pool',
	},

	'generic-pool': {
		active: true,
		balls: [
			{ key: 'cue', name: 'Cue Ball', path: 'ball-cue.svg' },
			{ key: 'ghost', name: 'Ghost Ball', path: 'ball-ghost.svg' },
			{ key: 'solid', name: 'Solid Ball', path: 'ball-solid-generic.svg' },
			{ key: 'stripe', name: 'Stripe Ball', path: 'ball-stripe-generic.svg' },
			{ key: 'red', name: 'Red Ball', path: 'ball-solid-generic-red.svg', color: '#FF0000' },
			{ key: 'yellow', name: 'Yellow Ball', path: 'ball-solid-generic-yellow.svg', color: '#FFFF00' },
			{ key: 'eight', name: '8 Ball', path: 'ball-solid-generic-8.svg', color: '#000000' },
		],
		description: 'Generic pool balls with solid and striped variations',
		directory: 'generic',
		gameType: 'pool',
		id: 'generic-pool',
		name: 'Generic Pool',
	},

	'snooker-standard': {
		active: true,
		balls: [
			{ key: 'cue', name: 'Cue Ball', path: 'ball-cue.svg' },
			{ key: 'ghost', name: 'Ghost Ball', path: 'ball-ghost.svg' },
			{ key: 'red', name: 'Red Ball', path: 'ball-red.svg', color: '#FF0000' },
			{ key: 'yellow', name: 'Yellow Ball', path: 'ball-yellow.svg', color: '#FFFF00' },
			{ key: 'green', name: 'Green Ball', path: 'ball-green.svg', color: '#008000' },
			{ key: 'brown', name: 'Brown Ball', path: 'ball-brown.svg', color: '#8B4513' },
			{ key: 'blue', name: 'Blue Ball', path: 'ball-blue.svg', color: '#0000FF' },
			{ key: 'pink', name: 'Pink Ball', path: 'ball-pink.svg', color: '#FFC0CB' },
			{ key: 'black', name: 'Black Ball', path: 'ball-black.svg', color: '#000000' },
		],
		description: 'Standard snooker balls with traditional colors',
		directory: 'snooker',
		gameType: 'snooker',
		id: 'snooker-standard',
		name: 'Snooker Standard',
	},

	'billiards-standard': {
		active: true,
		balls: [
			{ key: 'cue', name: 'Cue Ball', path: 'ball-cue.svg' },
			{ key: 'ghost', name: 'Ghost Ball', path: 'ball-ghost.svg' },
			{ key: 'red', name: 'Red Ball', path: 'ball-red.svg', color: '#FF0000' },
			{ key: 'yellow', name: 'Yellow Ball', path: 'ball-yellow.svg', color: '#FFFF00' },
		],
		description: 'Standard billiards balls for carom games',
		directory: 'billiards',
		gameType: 'billiards',
		id: 'billiards-standard',
		name: 'Billiards Standard',
	},
};

/**
 * Get all available ball sets
 *
 * @return IBallSetConfig[]
 */
export function getAllBallSets(): IBallSetConfig[] {
	return Object.values(BALL_SETS);
}

/**
 * Get active ball sets only
 *
 * @return IBallSetConfig[]
 */
export function getActiveBallSets(): IBallSetConfig[] {
	return Object.values(BALL_SETS).filter((set) => set.active);
}

/**
 * Get ball sets by game type
 *
 * @param gameType - The type of game to filter by
 * @return IBallSetConfig[]
 */
export function getBallSetsByGameType(gameType: 'pool' | 'snooker' | 'billiards' | 'carom'): IBallSetConfig[] {
	return Object.values(BALL_SETS).filter((set) => set.gameType === gameType && set.active);
}

/**
 * Get a specific ball set by ID
 *
 * @param id - The ball set ID
 * @return IBallSetConfig | null
 */
export function getBallSetById(id: string): IBallSetConfig | null {
	return BALL_SETS[id] || null;
}

/**
 * Get available ball set for a specific set and return the ball paths
 * This is a helper function that matches the current getter structure
 *
 * @param setId - The ball set ID
 * @return Record<string, string>
 */
export function getBallSetPaths(setId: string): Record<string, string> {
	const ballSet = getBallSetById(setId);

	if (!ballSet) {
		return {};
	}

	const paths: Record<string, string> = {};

	ballSet.balls.forEach((ball) => {
		paths[ball.key] = `${ballSet.directory}/${ball.path}`;
	});

	return paths;
}
